import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, Ruler, User } from "lucide-react";
import { useShop } from "../context/ShopContext";
import { useAuth } from "../context/AuthContext";
import { Logo } from "./Logo";

const links = [
  { to: "/women", label: "Women" },
  { to: "/men", label: "Men" },
  { to: "/swimwear", label: "Swimwear" },
  { to: "/fit-studio", label: "Fit Studio" },
];

export const Navbar = () => {
  const { cartCount, fitProfile } = useShop();
  const { user } = useAuth();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const solid = scrolled || !onHome;
  const textColor = "text-white";

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        solid ? "bg-ocean/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <button
            className={`lg:hidden ${textColor}`}
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-open"
            aria-label="Open menu"
          >
            <Menu size={22} strokeWidth={1.5} />
          </button>
          <div className="hidden lg:flex items-center gap-8">
            {links.slice(0, 3).map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={`overline ${textColor} hover:opacity-60 transition-opacity`}
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>

        <Link
          to="/"
          data-testid="nav-logo"
          className="absolute left-1/2 -translate-x-1/2 flex items-center"
        >
          <Logo tone="white" className="h-9 lg:h-11 w-auto" />
        </Link>

        <div className="flex items-center gap-5">
          <Link
            to="/fit-studio"
            data-testid="nav-fit-studio"
            className={`hidden lg:flex items-center gap-2 overline ${textColor} hover:opacity-60 transition-opacity`}
          >
            <Ruler size={16} strokeWidth={1.5} />
            {fitProfile ? `Fit · ${fitProfile.recommended_size}` : "Fit Studio"}
          </Link>
          <Link
            to={user ? "/account" : "/login"}
            data-testid="nav-account"
            className={`${textColor} hover:opacity-60 transition-opacity`}
            aria-label="Account"
          >
            <User size={22} strokeWidth={1.5} />
          </Link>
          <Link
            to="/cart"
            data-testid="nav-cart"
            className={`relative ${textColor} hover:opacity-60 transition-opacity`}
            aria-label="Cart"
          >
            <ShoppingBag size={22} strokeWidth={1.5} />
            {cartCount > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-2 -right-2 bg-sunkissed text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-sans"
              >
                {cartCount}
              </span>
            )}
          </Link>
        </div>
      </nav>

      {open && (
        <div className="fixed inset-0 z-50 bg-ocean text-sand lg:hidden" data-testid="mobile-menu">
          <div className="flex justify-between items-center h-20 px-6 border-b border-white/10">
            <Logo tone="white" className="h-8 w-auto" />
            <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu" className="text-white">
              <X size={24} strokeWidth={1.5} />
            </button>
          </div>
          <div className="flex flex-col px-6 py-10 gap-7">
            {links.map((l) => (
              <Link key={l.to} to={l.to} className="font-serif text-3xl text-sand" data-testid={`mobile-nav-${l.label.toLowerCase()}`}>
                {l.label}
              </Link>
            ))}
            <Link to={user ? "/account" : "/login"} className="font-serif text-3xl text-sand" data-testid="mobile-nav-account">
              {user ? "Account" : "Sign in"}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
